/**
 * Agent Roles Utilities
 * 
 * Manages access control for AI agent features.
 * Each agent has its own unique identifier that must be assigned to users.
 * 
 * Firebase User Structure:
 * {
 *   role: "admin" | "user" | "super_admin",  // Organizational role
 *   agents: ["logline_analysis_agent", ...]  // Array of AI agents user can access
 * }
 */

// Define all agent roles
export const AGENT_ROLES = {
  LOGLINE_ANALYSIS: 'logline_analysis_agent',
  // Future agents will be added here:
  // SCRIPT_COVERAGE: 'script_coverage_agent',
  // CHARACTER_ANALYSIS: 'character_analysis_agent',
};

/**
 * Check if user has access to a specific agent
 * @param {Object} userProfile - User profile object from AuthContext
 * @param {string} agentRole - The agent role to check (from AGENT_ROLES)
 * @returns {boolean} True if user has access
 */
export const hasAgentAccess = (userProfile, agentRole) => {
  if (!userProfile) return false;
  
  // Check if user has the specific agent in their agents array
  if (userProfile.agents && Array.isArray(userProfile.agents)) {
    return userProfile.agents.includes(agentRole);
  }
  
  return false;
};

/**
 * Check if user has access to ANY agent
 * Used to determine if "Agents" section should be visible in navigation
 * @param {Object} userProfile - User profile object from AuthContext
 * @returns {boolean} True if user has at least one agent
 */
export const hasAnyAgentAccess = (userProfile) => {
  if (!userProfile) return false;
  
  // Check if agents array exists and has at least one entry
  if (userProfile.agents && Array.isArray(userProfile.agents)) {
    return userProfile.agents.length > 0;
  }
  
  return false;
};

/**
 * Get list of agents the user has access to
 * @param {Object} userProfile - User profile object from AuthContext
 * @returns {Array} Array of agent identifiers user has access to
 */
export const getUserAgentRoles = (userProfile) => {
  if (!userProfile || !userProfile.agents || !Array.isArray(userProfile.agents)) {
    return [];
  }
  
  const allAgentRoles = Object.values(AGENT_ROLES);
  return userProfile.agents.filter(agent => allAgentRoles.includes(agent));
};

/**
 * Get friendly name for an agent role
 * @param {string} agentRole - The agent role (from AGENT_ROLES)
 * @returns {string} Human-readable agent name
 */
export const getAgentName = (agentRole) => {
  const agentNames = {
    [AGENT_ROLES.LOGLINE_ANALYSIS]: 'Logline Analysis',
    // Future agents:
    // [AGENT_ROLES.SCRIPT_COVERAGE]: 'Script Coverage',
    // [AGENT_ROLES.CHARACTER_ANALYSIS]: 'Character Analysis',
  };
  
  return agentNames[agentRole] || 'Unknown Agent';
};

